import { defineStore } from 'pinia';
import axiosInstance from '@/services/axiosInstance';
import { IUser } from '@/types';
import { useAppStore } from '@/stores/app/appStore';
import i18n from '@/plugins/i18n';

const { t } = i18n.global;



const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

export const useUserStore = defineStore('userStore', {
  state: () => ({
    token: null as string | null,
    user: null as IUser | null,
    subscription: null as PushSubscription | null,
    eventRecords: [] as { id: string; playByPlay: any[] }[],
  }),
  actions: {
    async setUser(data: { user: IUser, token: string }) {
      this.user = data.user;
      this.token = data.token;
      localStorage.setItem('token', data.token);
      await this.subscribeToPushNotifications();
    },
    setToken(newToken: string) {
      this.token = newToken;
      localStorage.setItem('token', newToken);
    },
    clearUser() {
      this.user = null;
      this.token = null;
      this.subscription = null;
      localStorage.removeItem('token');
    },
    async loadUserFromStorage() {
      const token = localStorage.getItem('token');
      if (token) {
        this.token = token;
        await this.fetchUserData();
      }
    },
    async fetchUserData() {
      try {
        //console.log('Načítám uživatelská data');
        const userId = this.getUserId;
        if (!userId) {
          throw new Error('User ID is not available');
        }
        const response = await axiosInstance.get(`${API_BASE_URL}/users/${userId}`);
        this.user = response.data;
        // Po úspěšném načtení uživatelských dat přihlásíme k push notifikacím
        //if (this.isMobileDevice()) {

        //this.subscribeToPushNotifications();

        //}
      } catch (error) {
        console.error('Chyba při načítání uživatelských dat:', error);
        this.clearUser();
      }
    },

    async subscribeToPushNotifications() {
      const publicKey = 'BFxWiuP7ktkwl13lJARYNsK9Z54eIMfJFf9GvehQr3egadRlqhvnhpuIf6G-TOEaTi_WyUn7YyFdMKYvAVstdaM';
      //console.log('VAPID Public Key:', publicKey);

      if (!publicKey) {
        console.error('VAPID veřejný klíč není definován.');
        return;
      }
      const convertedVapidKey = this.urlBase64ToUint8Array(publicKey);
      //console.log('Převedený VAPID klíč:', convertedVapidKey);

      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.register('/service-worker.js');
        //console.log('Service Worker je připraven:', registration);

        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: convertedVapidKey,
        });
        await this.sendSubscriptionToServer(subscription);

        //console.log('Push subscription byla úspěšně vytvořena:', subscription);
      } else {
        //console.warn('Service Worker není podporován tímto prohlížečem.');
      }
    },
    async sendSubscriptionToServer(subscription: PushSubscription) {
      try {
        //console.log('Odesílám subscription na server:', subscription);
        if (!this.user || !this.user._id) {
          throw new Error('User is not authenticated.');
        }
        await axiosInstance.post(`${API_BASE_URL}/users/notification-subscribe`, {
          subscription,
          userId: this.user._id,
        });
      } catch (error) {
        console.error('Chyba při odesílání subscription na server:', error);
      }
    },
    urlBase64ToUint8Array(base64String: string) {
      const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
      const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
      const rawData = window.atob(base64);
      const outputArray = new Uint8Array(rawData.length);
      for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
      }
      return outputArray;
    },
    isMobileDevice() {
      return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    },


    async clearEventRecordCache(eventId: string) {
      const before = this.eventRecords.length;
      // smaž celý záznam pro daný event
      this.eventRecords = this.eventRecords.filter(er => er.id !== eventId);
      const removed = before !== this.eventRecords.length;
      return removed; // můžeš využít v UI pro feedback
    },

    async clearEventRecordEntries(eventId: string) {
      const rec = this.eventRecords.find(er => er.id === eventId);
      if (rec) rec.playByPlay = [];
      return !!rec;
    },

    async updateGameStatus(eventId: string, status: any) {
      try {
        const appStore = useAppStore();
        appStore.showLoadingOverlay(t('updateGameStatus'));
        await axiosInstance.put(`${API_BASE_URL}/events/${eventId}/gamestatus`, status);
        console.log('Stav hry byl úspěšně aktualizován na serveru.', status);
      } catch (error) {
        console.error('Chyba při aktualizaci stavu hry na server:', error);
        // případně toast/snackbar
      } finally {
        const appStore = useAppStore();
        appStore.closeLoadingOverlay();
      }
    },

    // Upravená verze addEventRecord:
    async addEventRecord(eventId: string, record: any) {

      // 1) Najdi nebo vytvoř lokální eventRecord
      let eventRecord = this.eventRecords.find(er => er.id === eventId);
      if (!eventRecord) {
        eventRecord = { id: eventId, playByPlay: [] };
        this.eventRecords.push(eventRecord);
      }

      // 2) Deduplikace pro break záznamy
      if (record.type === 'break') {
        const exists = eventRecord.playByPlay.some(er =>
          er?.data?.breakType === record?.data?.breakType &&
          er?.data?.partNumber === record?.data?.partNumber
        );
        if (exists) return;
      }

      try {
        const appStore = useAppStore();
        appStore.showLoadingOverlay(t('addingEventRecord'));

        // 3) Sync na server
        await axiosInstance.post(
          `${API_BASE_URL}/events/${eventId}/playbyplayrecord`,
          record
        );

        // 4) Lokální přidání a setřídění
        eventRecord.playByPlay.push(record);
        eventRecord.playByPlay.sort((a, b) => (b?.rawTime ?? 0) - (a?.rawTime ?? 0));

      } catch (error) {
        console.error('Chyba při přidávání play-by-play záznamu na server:', error);
        record.addSyncErr = true;

        // i při chybě chceme mít lokální stopu
        eventRecord.playByPlay.push(record);
        eventRecord.playByPlay.sort((a, b) => (b?.rawTime ?? 0) - (a?.rawTime ?? 0));
      } finally {
        const appStore = useAppStore();
        appStore.closeLoadingOverlay();
      }
    },



    // Upravená verze removeEventRecord:
    async removeEventRecord(eventId: string, payload: { recordId: string, forceLocal: boolean }) {
      const eventRecord = this.eventRecords.find(er => er.id === eventId);


      if (eventRecord) {
        // Najdeme konkrétní záznam
        const record = eventRecord.playByPlay.find(r => r.id === payload.recordId);
        try {
          // Pokus o synchronizaci odebrání na backendu (endpoint: POST /events/:id/playbyplayrecord/:recordId)
          await axiosInstance.delete(`${API_BASE_URL}/events/${eventId}/playbyplayrecord/${payload.recordId}`);
          // Odebrání záznamu ze store při úspěšné synchronizaci
          eventRecord.playByPlay = eventRecord.playByPlay.filter(r => r.id !== payload.recordId);
          if (eventRecord.playByPlay.length === 0) {
            this.eventRecords = this.eventRecords.filter(er => er.id !== eventId);
          }
        } catch (error) {
          console.error('Chyba při odebírání play-by-play záznamu na server:', error);
          // Při selhání aktualizujeme záznam s flagem, že se odstranění synchronizovat nepovedlo
          if (record) {
            record.deleteSyncErr = true;
          }
          if (payload.forceLocal) {
            eventRecord.playByPlay = eventRecord.playByPlay.filter(r => r.id !== payload.recordId);
            if (eventRecord.playByPlay.length === 0) {
              this.eventRecords = this.eventRecords.filter(er => er.id !== eventId);
            }
          }
        }
      }
    },

    // Nová metoda playByPlayResend:
    async playByPlayResend(eventId: string, recordId: string) {
      // Vyhledání eventRecord ve store podle eventId
      const eventRecord = this.eventRecords.find(er => er.id === eventId);
      if (!eventRecord) {
        console.error(`Event record pro id ${eventId} nebyl nalezen.`);
        return;
      }

      // Vyhledání konkrétního záznamu podle recordId
      const record = eventRecord.playByPlay.find(r => r.id === recordId);
      if (!record) {
        console.error(`Záznam s recordId ${recordId} nebyl nalezen v event record.`);
        return;
      }

      try {
        // Pokus o opětovné odeslání záznamu na backend
        await axiosInstance.post(`${API_BASE_URL}/events/${eventId}/playbyplayrecord`, record);
        // Pokud bylo odeslání úspěšné, odstraníme flag synchronizační chyby, pokud existuje
        if (record.addSyncErr) {
          delete record.addSyncErr;
        }
        console.log('Play-by-play záznam byl úspěšně odeslán znovu.');
      } catch (error) {
        console.error('Chyba při odesílání play-by-play záznamu na server:', error);
        // Nastavíme flag, že synchronizace se opět nezdařila
        record.addSyncErr = true;
      }
    },

    // Nová metoda v userStore pro synchronizaci play-by-play záznamů:
    // Nová metoda v userStore pro synchronizaci play-by-play záznamů:
    async syncEventRecords(eventId: string, serverRecords: any[]) {
      // 0) Normalizace vstupu - i kdyby přišlo null/undefined/nepole
      const srv = Array.isArray(serverRecords) ? serverRecords : [];

      // 1) Najdi / vytvoř lokální eventRecord
      let eventRecord = this.eventRecords.find(er => er.id === eventId);
      if (!eventRecord) {
        this.eventRecords.push({
          id: eventId,
          playByPlay: srv.slice(), // klon
        });
        return;
      }
      if (!Array.isArray(eventRecord.playByPlay)) {
        eventRecord.playByPlay = [];
      }

      // 2) Mapa serverových záznamů podle id
      const serverRecordsMap = new Map<string, any>();
      for (const sr of srv) {
        if (sr && sr.id) serverRecordsMap.set(String(sr.id), sr);
      }

      // 3) Sloučení: lokální → server
      for (const localRecord of eventRecord.playByPlay) {
        const lid = localRecord?.id ? String(localRecord.id) : undefined;

        if (lid && serverRecordsMap.has(lid)) {
          // existuje i na serveru → přepiš lokální daty ze serveru
          Object.assign(localRecord, serverRecordsMap.get(lid));
          delete localRecord.addSyncErr;
          serverRecordsMap.delete(lid);
        } else {
          // neznáme serverovou verzi nebo lokální bez id → označ sync chybu
          localRecord.addSyncErr = true;
        }
      }

      // 4) Zbylé serverové záznamy přidej
      for (const record of serverRecordsMap.values()) {
        eventRecord.playByPlay.push(record);
      }

      // 5) Seřadit bezpečně podle rawTime (s fallbackem)
      eventRecord.playByPlay.sort((a, b) => (b?.rawTime ?? 0) - (a?.rawTime ?? 0));
    },


    // uvnitř actions: { ... }
    hasRole(admins?: Array<string | { _id?: string } | any>): boolean {
      const me = this.user?._id;
      if (!me) return false;

      // 1) globální admin
      const roles: string[] = Array.isArray((this.user as any)?.roles) ? (this.user as any).roles : [];
      if (roles.includes('administrator')) return true;

      // 2) je v seznamu admins (string / {_id} / ObjectId)
      if (!Array.isArray(admins)) return false;

      const myId = String(me);
      return admins.some((a) => {
        if (!a) return false;
        if (typeof a === 'string') return a === myId;

        // {_id: '...'} nebo cokoliv co má _id
        if (typeof a === 'object' && a._id) return String(a._id) === myId;

        // Mongoose ObjectId apod.
        try { return String(a) === myId; } catch { return false; }
      });
    },
  },
  getters: {
    isAuthenticated: (state) => !!state.token,
    getUserName: (state) => state.user?.name || '',
    getUserId: (state) => state.user?._id || '',
    // Parametrizovaný getter – vrátí eventRecord podle jeho id
    getEventRecordById: (state) => {
      return (id: string) => state.eventRecords.find(record => record.id === id);
    },
  },
  persist: {
    enabled: true,
    strategies: [
      {
        key: 'userStore',
        storage: localStorage,
      },
    ],
  },
});
